import React, { memo, useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { useRecoilValue } from 'recoil';

import { Icon, IconButton } from 'components';
import { Button } from 'components/form';
import ActionMenu from 'components/ActionMenu';
import { Row } from 'components/layout';
import { ButtonV3 } from 'components/ComponentV2';
import { AISearchSuggestionBanner } from 'components/AISearchSuggestionBanner/AISearchSuggestionBanner.component';
import { useResponsive } from 'hooks/useResponsive';
import { useCandidate } from 'hooks/useCandidate';
import { useApp } from 'lib/contexts/save-in-app-context';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import { PAGE_ROUTES } from 'lib/page-routes';
import { ApplicationResponseModalEnum } from 'lib/models/candidate-applications';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { useCandidateDashboard } from 'lib/providers/CandidateDashboardProvider';
import { useAuth } from 'lib/providers/AuthProvider';

import styles from './DashboardBanner.module.scss';

export interface DashboardBannerProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

export const DashboardBanner: React.FC<DashboardBannerProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: DashboardBannerProps) => {
    const router = useRouter();
    const screens = useResponsive();
    const { candidate, isCandidate } = useAuth();

    const { headers } = useRecoilValue(httpHeadersState);

    const contentClassNames = cn(
      {
        [styles.DashboardBanner]: true,
      },
      className
    );

    const [hideBanner, setHideBanner] = useState(false);
    const [showAISearchSuggestionBanner, setShowAISearchSuggestionBanner] =
      useState(isCandidate);

    const { setWriteApplicationModalType } = useApp();
    const { candidateDashboard } = useCandidateDashboard();
    const {
      updateLanguageBanner,
      updateCVBuilderBanner,
      redirectToCoverLetterBuilder,
      updateCandidateInfo,
    } = useCandidate();

    const { profile_complete, started_or_matches_applications = [] } =
      candidateDashboard || {};
    const {
      languages: languagesResponse,
      cities: citiesResponse,
      visa_country_ids: candidateVisaPreference,
      show_add_language_banner,
      show_cv_builder_banner,
    } = candidate || {};

    const candidateLanguages = useMemo(() => {
      const languages = parseArrayResponse(languagesResponse);
      return languages.map((item) => item.id);
    }, [languagesResponse]);

    const candidateLocationCountries = useMemo(() => {
      const cities = parseArrayResponse(citiesResponse);
      return cities
        ?.map((city) => {
          const country = parseResponse(city.country);
          return {
            id: country.id,
            name: country.name,
          };
        })
        ?.filter(
          (item, index, countries) =>
            countries.findIndex((value) => item.id === value.id) === index
        );
    }, [citiesResponse]);

    const toggleAddLanguageBanner = async () => {
      setHideBanner(true);
      await updateLanguageBanner({ headers, show_add_language_banner: false });
    };

    const toggleCVBuilderBanner = async () => {
      setHideBanner(true);
      await updateCVBuilderBanner({ show_cv_builder_banner: false });
    };

    const onCloseAISearchSuggestionBanner = useCallback(() => {
      setShowAISearchSuggestionBanner(false);
    }, []);

    const setVisaPreference = async (visa_country_ids: Array<string>) => {
      await updateCandidateInfo({
        partialCandidateAttributes: { visa_country_ids },
      });
    };

    const actionMenuSubOptions = useMemo(
      () =>
        started_or_matches_applications?.map(
          ({ touchpoint_id, touchpoint_title }) => {
            return {
              id: touchpoint_id,
              label: touchpoint_title,
              onClick: () => redirectToCoverLetterBuilder(touchpoint_id),
            };
          }
        ),
      [started_or_matches_applications]
    );

    const actionMenuOptions = [
      {
        id: '1',
        label: 'Get questions replies',
        onClick: () => {
          setWriteApplicationModalType({
            type: ApplicationResponseModalEnum.questionResponse,
            application: null,
          });
        },
      },
      {
        id: '2',
        label: 'Write cover letter',
        options: actionMenuSubOptions,
      },
    ];

    const getBanner = () => {
      if (!profile_complete) {
        return (
          <div
            className={contentClassNames}
            style={style}
            data-testid="DashboardBanner"
          >
            <div className={styles.content}>
              <Icon
                iconName="icon_info"
                size={screens.sm ? 'large' : 'medium'}
              />
              <p>
                You need to complete your profile to be visible to recruiters.
              </p>
            </div>
            <Row
              align="center"
              justify="space-between"
              columnGap={8}
              className={styles.buttonsRow}
            >
              <ButtonV3
                size="small"
                onClick={() => router.push(PAGE_ROUTES.CANDIDATE_PROFILE)}
              >
                Complete profile
              </ButtonV3>
            </Row>
          </div>
        );
      } else if (
        candidateLanguages &&
        candidateLanguages.length === 0 &&
        show_add_language_banner &&
        !hideBanner
      ) {
        return (
          <div
            className={contentClassNames}
            style={style}
            data-testid="DashboardBanner"
          >
            <div className={styles.content}>
              <Icon
                iconName="icon_info"
                size={screens.sm ? 'large' : 'medium'}
              />
              <p>
                Add languages that you are comfortable working in and get
                noticed by recruiters.
              </p>
              <IconButton
                iconName="icon_close"
                onClick={toggleAddLanguageBanner}
                className={cn(styles.hideDesktop, styles.closeIcon)}
              />
            </div>
            <Row
              align="center"
              justify="space-between"
              columnGap={8}
              className={styles.buttonsRow}
            >
              <ButtonV3
                size="small"
                onClick={() =>
                  router.push(
                    `${PAGE_ROUTES.CANDIDATE_ONBOARDING}?matching_preference=true&step=language`
                  )
                }
              >
                Add languages
              </ButtonV3>
              <IconButton
                iconName="icon_close"
                onClick={toggleAddLanguageBanner}
                className={cn(
                  styles.hideMobile,
                  styles.hideTablet,
                  styles.closeIcon
                )}
              />
            </Row>
          </div>
        );
      } else if (candidateVisaPreference === null) {
        /** When multiple country is selected on Locations preference
         * show country list
         * When single country is selected on Locations, show yes/no
         */
        if (
          candidateLocationCountries &&
          candidateLocationCountries?.length !== 1
        ) {
          return (
            <div
              className={contentClassNames}
              style={style}
              data-testid="DashboardBanner"
            >
              <div className={styles.content}>
                <Icon
                  iconName="icon_info"
                  size={screens.sm ? 'large' : 'medium'}
                />
                <p>Which countries do you require a work visa for?</p>
              </div>
              <Row
                align="center"
                justify="space-between"
                columnGap={8}
                className={styles.buttonsRow}
              >
                <ButtonV3
                  size="small"
                  onClick={() =>
                    router.push(
                      `${PAGE_ROUTES.CANDIDATE_ONBOARDING}?matching_preference=true&step=visa_preference`
                    )
                  }
                >
                  Add visa info
                </ButtonV3>
              </Row>
            </div>
          );
        } else {
          return (
            <div
              className={contentClassNames}
              style={style}
              data-testid="DashboardBanner"
            >
              <div className={cn(styles.content, styles.contentNoClose)}>
                <Icon
                  iconName="icon_info"
                  size={screens.sm ? 'large' : 'medium'}
                />
                <p>
                  Do you require a visa sponsorship for{' '}
                  {candidateLocationCountries[0].name}?
                </p>
              </div>
              <Row
                align="center"
                justify="space-between"
                columnGap={8}
                className={styles.buttonsRow}
              >
                <Button
                  className={styles.whitebutton}
                  onClick={() =>
                    setVisaPreference([candidateLocationCountries[0].id])
                  }
                  color="white"
                  showOnlySpinner={true}
                >
                  Yes
                </Button>
                <Button
                  className={styles.whitebutton}
                  onClick={() => setVisaPreference([])}
                  color="white"
                  showOnlySpinner={true}
                >
                  No
                </Button>
              </Row>
            </div>
          );
        }
      } else if (showAISearchSuggestionBanner) {
        return (
          <AISearchSuggestionBanner
            onClose={onCloseAISearchSuggestionBanner}
            className={cn(
              contentClassNames,
              styles['ai-search-suggestion-banner']
            )}
            contentClassName={styles['ai-search-suggestion-banner-content']}
          />
        );
      } else if (!show_cv_builder_banner && !hideBanner) {
        return (
          <div
            className={cn(
              styles.DashboardBanner,
              styles.applicationbanner,
              className
            )}
            style={style}
            data-testid="DashboardBanner"
          >
            <div className={cn(styles.content, styles.contentApplications)}>
              <Icon
                iconName="icon_magic-wand"
                size={screens.sm ? 'large' : 'medium'}
              />
              <p>
                We can help you build CV, write cover letter or reply to
                application questions.
              </p>
              <IconButton
                iconName="icon_close"
                onClick={toggleCVBuilderBanner}
                className={cn(styles.hideDesktop, styles.closeIcon)}
              />
            </div>
            <Row
              align="center"
              justify="space-between"
              columnGap={8}
              className={styles.buttonsRow}
            >
              <Button
                className={cn(styles.whitebutton, styles.fullWidth)}
                onClick={() => router.push(PAGE_ROUTES.CANDIDATE_PROFILE)}
                color="white"
              >
                Build CV
              </Button>
              <ActionMenu
                options={actionMenuOptions}
                className={styles.fullWidth}
                triggerButtonClassName={cn(styles.actionmenu)}
                subOptionsClassName={styles.actionmenuSub}
                actionElement={
                  <Button
                    className={styles.whitebutton}
                    color="white"
                    isFullWidth
                  >
                    Write applications
                  </Button>
                }
                listClassName={styles.actionmenuList}
                align={'auto'}
              />
              <IconButton
                iconName="icon_close"
                onClick={toggleCVBuilderBanner}
                className={cn(
                  styles.hideMobile,
                  styles.hideTablet,
                  styles.closeIcon
                )}
              />
            </Row>
          </div>
        );
      } else return null;
    };

    return <>{candidate && candidateDashboard ? getBanner() : null}</>;
  }
);

DashboardBanner.displayName = 'DashboardBanner';
